import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FormField,
  StatusChip,
  TextInput,
  Textarea,
} from '../../components/primitives.jsx';
import { api } from '../../lib/apiClient.js';
import { useMutation } from '../../lib/useApi.js';
import { toast } from '../../lib/useToast.js';
import { useSiteAccess } from '../../app/SiteAccessContext.jsx';
import './siteAccessPortal.css';

const REQUESTABLE_STATUSES = new Set([null, 'pending', 'denied']);

function parseDnAttribute(subject, attribute) {
  if (!subject) {
    return null;
  }
  const pattern = new RegExp(`(?:^|,)\\s*${attribute}=((?:\\\\.|[^,])*)`, 'i');
  const match = pattern.exec(subject);
  return match?.[1]?.replace(/\\,/g, ',').trim() || null;
}

function buildDraft(state) {
  return {
    fullName: state?.fullName || '',
    email: state?.email || '',
    organization: state?.organization || '',
    justification: state?.justification || '',
  };
}

function isDirectoryUnavailable(state) {
  return state?.gateMode === 'enforced' && state?.directoryState === 'unavailable';
}

function portalStatusKey(state) {
  if (isDirectoryUnavailable(state)) {
    return 'directory-unavailable';
  }
  if (!state?.status) {
    return 'no-record';
  }
  return state.status;
}

function submitButtonLabel(status) {
  if (status === 'pending') {
    return 'Update request';
  }
  if (status === 'denied') {
    return 'Submit updated request';
  }
  return 'Request access';
}

function portalCopy(statusKey) {
  switch (statusKey) {
    case 'pending':
      return {
        title: 'Your site-access request is pending review',
        description:
          'Your CAC identity has been captured and the request is waiting for an approver to review it.',
      };
    case 'denied':
      return {
        title: 'Your previous request was denied',
        description:
          'You can submit a fresh request here with updated context for the approver team.',
      };
    case 'suspended':
      return {
        title: 'Your site access is temporarily suspended',
        description:
          'A site-access approver must restore your access before you can continue into DHA PRIME.',
      };
    case 'revoked':
      return {
        title: 'Your site access has been revoked',
        description:
          'A site-access approver must create or approve a new access path before you can continue.',
      };
    case 'approved':
      return {
        title: 'Your CAC identity is approved for DHA PRIME',
        description:
          'Continue to the persona login page to choose the role you need for this session.',
      };
    case 'directory-unavailable':
      return {
        title: 'Site-access review is temporarily unavailable',
        description:
          'The site-access directory is unavailable right now. You can review your identity details below, but new requests cannot be submitted until the service recovers.',
      };
    default:
      return {
        title: 'Request site access for DHA PRIME',
        description:
          'PMHub access is limited to approved CAC holders. Submit your request below so an approver can review your mission need.',
      };
  }
}

export function SiteAccessPortalPage() {
  const location = useLocation();
  const { error, state, refresh } = useSiteAccess();
  const mutation = useMutation();
  const [draft, setDraft] = useState(() => buildDraft(state));

  useEffect(() => {
    setDraft(buildDraft(state));
  }, [state?.email, state?.fullName, state?.justification, state?.organization, state?.status]);

  const statusKey = portalStatusKey(state);
  const copy = portalCopy(statusKey);
  const certCommonName = parseDnAttribute(state?.certSubject, 'CN');
  const showRequestForm = REQUESTABLE_STATUSES.has(state?.status);
  const outage = isDirectoryUnavailable(state);
  const fromStatusShare = Boolean(location.state?.fromStatusShare);
  const canSubmit =
    showRequestForm &&
    !outage &&
    draft.fullName.trim() &&
    draft.email.trim() &&
    draft.organization.trim() &&
    draft.justification.trim();

  const details = useMemo(
    () => [
      { label: 'EDIPN', value: state?.edipn || 'Not available' },
      { label: 'Certificate name', value: certCommonName || 'Not available' },
    ],
    [certCommonName, state?.edipn],
  );

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit || mutation.running) {
      return;
    }
    try {
      await mutation.run(() =>
        api.post('/site-access/request', {
          fullName: draft.fullName.trim(),
          email: draft.email.trim(),
          organization: draft.organization.trim(),
          justification: draft.justification.trim(),
        }),
      );
      await refresh();
      toast.success('Your site-access request was saved.');
    } catch (submitError) {
      toast.error(submitError?.message || 'Could not save your site-access request.');
    }
  }

  return (
    <div className="site-access-portal-shell" data-testid="site-access-portal">
      <section className="site-access-portal-hero">
        <div className="site-access-portal-brand">
          <div className="site-access-portal-brand__assets">
            <img src="/brand/dha-seal.svg" alt="DHA emblem" className="site-access-portal-seal" />
          </div>
          <div>
            <div className="site-access-portal-kicker">DHA PRIME Site Access</div>
            <h1>{copy.title}</h1>
          </div>
        </div>
        <p className="site-access-portal-lede">{copy.description}</p>
        {fromStatusShare ? (
          <p className="site-access-portal-share-note">
            Status-share links are also limited to approved CAC holders.
          </p>
        ) : null}
        {error ? (
          <div className="banner amber" role="status">
            <div>
              <strong>Client note</strong>
              <p>
                The site-access status feed reported an error: {error.message}. The server
                remains the source of truth for enforcement.
              </p>
            </div>
          </div>
        ) : null}
        <ul className="site-access-portal-bullets">
          <li>Approved users continue to the existing persona-based login flow.</li>
          <li>Requests are reviewed against your CAC identity, not the form body alone.</li>
          <li>Suspended or revoked access must be restored by a site-access approver.</li>
        </ul>
      </section>

      <section className="site-access-portal-card">
        <div
          className="site-access-portal-card__status"
          data-testid={`portal-status-${statusKey}`}
        >
          <div className="site-access-portal-card__status-head">
            <div>
              <div className="eyebrow">Current state</div>
              <h2>{copy.title}</h2>
            </div>
            <StatusChip status={statusKey === 'directory-unavailable' ? 'blocked' : statusKey} />
          </div>
          {state?.decisionNote ? (
            <div className="site-access-portal-note">
              <strong>Reviewer note</strong>
              <p>{state.decisionNote}</p>
            </div>
          ) : null}
          {state?.justification && state?.status === 'pending' ? (
            <div className="site-access-portal-note">
              <strong>Last submitted justification</strong>
              <p>{state.justification}</p>
            </div>
          ) : null}
        </div>

        <div className="site-access-identity-card">
          <div className="eyebrow">CAC identity</div>
          <dl className="site-access-identity-list">
            {details.map((detail) => (
              <div key={detail.label} className="site-access-identity-item">
                <dt>{detail.label}</dt>
                <dd className="mono">{detail.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        {statusKey === 'approved' ? (
          <div className="site-access-portal-actions">
            <Link to="/login" className="btn primary">
              Continue to login
            </Link>
          </div>
        ) : null}

        {showRequestForm ? (
          <form
            className="site-access-portal-form"
            data-testid="portal-request-form"
            onSubmit={handleSubmit}
          >
            <FormField label="Full name" htmlFor="portal-full-name" required>
              <TextInput
                id="portal-full-name"
                value={draft.fullName}
                onChange={(value) => setDraft((current) => ({ ...current, fullName: value }))}
                disabled={mutation.running}
              />
            </FormField>
            <FormField label="Email" htmlFor="portal-email" required>
              <input
                id="portal-email"
                type="email"
                className="form-input"
                value={draft.email}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, email: event.target.value }))
                }
                disabled={mutation.running}
              />
            </FormField>
            <FormField label="Organization" htmlFor="portal-organization" required>
              <TextInput
                id="portal-organization"
                value={draft.organization}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, organization: value }))
                }
                disabled={mutation.running}
              />
            </FormField>
            <FormField label="Justification" htmlFor="portal-justification" required>
              <Textarea
                id="portal-justification"
                value={draft.justification}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, justification: value }))
                }
                rows={5}
                disabled={mutation.running}
              />
            </FormField>
            {mutation.error ? (
              <div className="banner red" role="alert">
                <div>
                  <strong>Request not saved</strong>
                  <p>{mutation.error.message}</p>
                </div>
              </div>
            ) : null}
            <div className="site-access-portal-actions">
              <button
                type="submit"
                className="btn primary"
                data-testid="portal-submit"
                disabled={!canSubmit || mutation.running}
              >
                {mutation.running ? 'Saving request…' : submitButtonLabel(state?.status)}
              </button>
            </div>
          </form>
        ) : null}
      </section>
    </div>
  );
}
